import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalBroadcastService
} from '@azure/msal-angular';
import { AuthService } from '../services/auth.service';
import { Subject, Subscription } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig, teamConfig } from './sso.config';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { ToastrService } from 'ngx-toastr';
import { UtilsService } from '../services/utils.service';
import { Location } from '@angular/common';
import { AuthenticationResult, BrowserUtils, EventMessage, EventType, InteractionStatus, PopupRequest } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-login',
    template: '',
    styleUrls: [],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private location: Location,
    private authServiceApp: AuthService,
    private oauthService: OAuthService,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private toastr: ToastrService) {
    this.route.queryParams.subscribe(async params => {
      if (this.utilsService.inPortal()) {
        if (params['code']) {
          this.authServiceApp.teamAuth(params).subscribe(data => {
            this.authServiceApp.login('team', data).subscribe(authenticated => {
              if (authenticated) this.router.navigateByUrl('/invoices');
              else {
                this.toastr.error('error while loading TEAM account');
                this.authServiceApp.clearStores();
                window.location.replace(window.location.origin);
              }
            }, err => {
              this.authServiceApp.clearStores();
              this.toastr.error(err);
              window.location.replace(window.location.origin);
              throw Error(err);
            })
          }, err => {
            this.authServiceApp.clearStores();
            this.toastr.error(err);
            throw Error(err);
          });
        }
        else {
          try {
            await this.configureSingleSignOn();
          } catch {
            this.toastr.error('Error while loading TEAM configuration');
            return;
          }
          this.submit();
        }
      }
    });
  }

  ngOnInit() {
    const currentPath = this.location.path();
    this.isIframe = BrowserUtils.isInIframe() && !window.opener && currentPath.indexOf("logout") < 0;
    this.setLoginDisplay();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      })


    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
        this.authServiceApp.login('AD', payload).subscribe(authenticated => {
          if (authenticated) {
            window.location.replace(window.location.origin);
          }
          else {
            this.toastr.error('Error while auth.');
            setTimeout(() => {
              this.authServiceApp.logout('AD');
            }, 3000);
          }
        }, err => {
          throw Error(err);
        })
      });

    if (!this.utilsService.inPortal()) {
      if (!this.loginDisplay) {
        this.submit();
      }
      else {
        this.router.navigateByUrl('/invoices');
      }
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService.loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  submit() {
    if (this.utilsService.inPortal()) {
      this.oauthService.initImplicitFlow();
    }
    else {
      this.authServiceApp.loginRedirectAd();
    }
  }

  async configureSingleSignOn(): Promise<void> {
    const code_verifier = this.utilsService.makeString(128);
    const challenge = await this.authServiceApp.generateCodeChallenge(code_verifier);
    this.oauthService.configure(authConfig(challenge));
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    await this.oauthService.loadDiscoveryDocument(teamConfig.discoveryUrl);
    sessionStorage.setItem('c_ver', code_verifier);
  }

}
